import React from 'react';
import { isMoveToManagementRouterEnabled } from 'feature-flags';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { useLazyLoadQuery, graphql } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import AiIcon from '@atlaskit/icon/core/ai-agent';
import { UI_EVENT_TYPE, ContextualAnalyticsData, SCREEN } from '@atlassian/analytics-bridge';
import { HelpHelpCenterAri } from '@atlassian/ari';
import { HELP_CENTERS_MANAGEMENT_BASE_PATH, ScreenName } from '@atlassian/help-center-common-component/constants';

import { FireScreenEventOnMount } from '@atlassian/help-center-common-util/analytics/fire-screen-event';
import { history } from '@atlassian/help-center-common-util/history';
import { getHelpCenterAri, getCloudId } from '@atlassian/help-center-common-util/meta';
import { DynamicThemingButton } from '../dynamic-theming-button';
import type { virtualServiceAgentHeaderDumbQuery } from './__generated__/virtualServiceAgentHeaderDumbQuery.graphql';
import messages from './messages';

export interface Props {
    isJiraAdmin: boolean;
}
const VirtualServiceAgentHeaderDumb = ({ isJiraAdmin }: Props) => {
    di(getHelpCenterAri);
    di(getCloudId);
    const { formatMessage } = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const cloudId = getCloudId();
    const helpCenterAri = getHelpCenterAri();
    const virtualAgentEnabledData = useLazyLoadQuery<virtualServiceAgentHeaderDumbQuery>(
        graphql`
            query virtualServiceAgentHeaderDumbQuery($cloudId: ID!, $helpCenterAri: ID!) {
                helpCenter(cloudId: $cloudId) @optIn(to: "HelpCenterAggBeta") {
                    helpCenterById(helpCenterAri: $helpCenterAri) {
                        __typename
                        ... on HelpCenter {
                            virtualAgentEnabled
                        }
                    }
                }
            }
        `,
        {
            cloudId,
            helpCenterAri,
        }
    );
    const isVirtualAgentEnabled =
        virtualAgentEnabledData?.helpCenter?.helpCenterById?.__typename === 'HelpCenter' &&
        virtualAgentEnabledData.helpCenter.helpCenterById.virtualAgentEnabled;
    const handleVirtualAgentMenuClick = () => {
        createAnalyticsEvent({
            analyticsType: UI_EVENT_TYPE,
            action: 'clicked',
            actionSubjectId: 'virtualServiceAgentButton',
        }).fire();
        if (isMoveToManagementRouterEnabled()) {
            const { helpCenterId } = HelpHelpCenterAri.parse(helpCenterAri);
            window.location.href = `${window.location.origin}${HELP_CENTERS_MANAGEMENT_BASE_PATH}/${helpCenterId}/virtual-agent`;
        } else {
            history.push('/virtual-agent');
        }
    };
    if (!isJiraAdmin || isVirtualAgentEnabled) {
        return null;
    }
    return (
        <ContextualAnalyticsData sourceName={ScreenName.VIRTUAL_SERVICE_AGENT_HEADER} sourceType={SCREEN}>
            <DynamicThemingButton
                label={formatMessage(messages.virtualServiceAgent)}
                icon={<AiIcon label={formatMessage(messages.virtualServiceAgent)} />}
                onClick={handleVirtualAgentMenuClick}
            />
            <FireScreenEventOnMount />
        </ContextualAnalyticsData>
    );
};
export default VirtualServiceAgentHeaderDumb;
